import { useState } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { api, call, ApiError } from '../lib/api'
import { directoryQuery, meQuery, portalQuery } from '../lib/queries'
import { useRealtime } from '../lib/realtime'
import { CYLINDER_SIZES } from '../lib/options'
import { ago, kg, naira, nairaFull, orderDate } from '../lib/format'
import { Ring } from '../components/charts'
import { FormError } from '../components/auth'
import { Logo, Topbar } from '../components/shell'
import { StatusPill, type Status } from '../components/status'
import { Button, Card, Field, Input, Modal, Select, cx, levelWord, toast } from '../components/ui'

// Customer portal: every cylinder this person owns, across any number of gas businesses, live. Staff can use it too.
export const Route = createFileRoute('/portal')({
  validateSearch: (s: Record<string, unknown>): { c?: string } => ({ c: typeof s.c === 'string' ? s.c : undefined }),
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery)
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) throw redirect({ to: '/login', search: { next: '/portal' } })
      throw e
    }
  },
  loader: ({ context }) => context.queryClient.ensureQueryData(portalQuery),
  component: Portal,
})

type Cyl = Awaited<ReturnType<NonNullable<typeof portalQuery.queryFn>>>['cylinders'][number] & { liveFlow?: number }
const ringColor = (c: Cyl) => (c.levelKg <= c.triggerKg ? '#f86822' : c.level === 'full' ? '#2cc76c' : '#e8a33d')

function Portal() {
  const live = useRealtime('portal')
  const { data } = useSuspenseQuery(portalQuery)
  const navigate = Route.useNavigate()
  const selectedId = Route.useSearch().c
  const [adding, setAdding] = useState(false)
  const cylinders = data.cylinders as Cyl[]
  const sel = cylinders.find((c) => c.id === selectedId) ?? cylinders[0]

  return (
    <div className="min-h-dvh">
      <div className="flex items-center bg-white px-4 py-3 sm:px-6 lg:px-10">
        <Logo className="h-9 w-auto" />
        <div className="-mt-4 flex-1 lg:-mt-7"><Topbar live={live} search={false} bell={false} /></div>
      </div>
      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Hi {data.user.firstName}</h1>
            <p className="mt-1 text-sm text-ink-2">{cylinders.length ? `${cylinders.length} cylinder${cylinders.length > 1 ? 's' : ''} on GasRail` : 'Add your first cylinder to get started.'}</p>
          </div>
          <Button onClick={() => setAdding(true)}>+ Add cylinder</Button>
        </div>

        {cylinders.length > 1 && (
          <div className="mt-6 flex gap-3 overflow-x-auto pb-1" role="tablist" aria-label="Your cylinders">
            {cylinders.map((c) => (
              <button
                key={c.id}
                role="tab"
                aria-selected={c.id === sel?.id}
                onClick={() => navigate({ search: { c: c.id }, replace: true })}
                className={cx('flex min-w-[190px] items-center gap-3 rounded-card border-2 bg-card p-3 text-left', c.id === sel?.id ? 'border-brand' : 'border-transparent hover:border-black/10')}
              >
                <span className="size-3 flex-none rounded-full" style={{ background: c.status === 'pending' ? '#c4c4c4' : ringColor(c) }} />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold">{c.label || `${kg(c.cylinderKg)} cylinder`}</span>
                  <span className="block truncate text-xs text-muted">{c.business.name} · {c.status === 'pending' ? 'awaiting approval' : `${c.levelKg.toFixed(3)}kg left`}</span>
                </span>
              </button>
            ))}
          </div>
        )}

        {sel ? <CylinderView key={sel.id} c={sel} /> : (
          <Card className="mt-8 p-10 text-center">
            <p className="font-semibold">No cylinders yet</p>
            <p className="mx-auto mt-1 max-w-md text-sm text-ink-2">Add your cylinder and choose the gas business and outlet that supplies it. They'll approve it and fit a meter so you can watch your gas level live.</p>
            <Button className="mt-5" onClick={() => setAdding(true)}>Add cylinder</Button>
          </Card>
        )}
      </main>
      <Modal open={adding} onClose={() => setAdding(false)} title="Add a cylinder">
        <AddCylinder onDone={(id) => (setAdding(false), navigate({ search: { c: id }, replace: true }))} />
      </Modal>
    </div>
  )
}

function CylinderView({ c }: { c: Cyl }) {
  const qc = useQueryClient()
  const navigate = Route.useNavigate()
  const open = c.orders.find((o) => ['pending', 'approved', 'in_progress'].includes(o.status))
  const room = Math.max(0, Math.round((c.cylinderKg - c.levelKg) * 10) / 10)
  const [amount, setAmount] = useState(String(room || c.cylinderKg))
  const refresh = () => qc.invalidateQueries({ queryKey: ['portal'] })
  const refill = useMutation({
    mutationFn: () => call(api.portal.cylinders[':id'].refill.$post({ param: { id: c.id }, json: { kg: Number(amount) || undefined } })),
    onSuccess: () => (toast(`Refill requested — ${c.outlet.name} has been notified`), refresh()),
    onError: (e) => toast(e.message, 'error'),
  })
  const cancel = useMutation({
    mutationFn: (id: string) => call(api.portal.orders[':id'].cancel.$post({ param: { id } })),
    onSuccess: () => (toast('Request cancelled'), refresh()),
    onError: (e) => toast(e.message, 'error'),
  })
  const remove = useMutation({
    mutationFn: () => call(api.portal.cylinders[':id'].$delete({ param: { id: c.id } })),
    onSuccess: () => (toast(c.status === 'pending' ? 'Request withdrawn' : 'Cylinder removed from your account'), navigate({ search: {}, replace: true }), refresh()),
  })
  const rename = useMutation({
    mutationFn: (label: string) => call(api.portal.cylinders[':id'].$patch({ param: { id: c.id }, json: { label: label || null } })),
    onSuccess: refresh,
  })
  const low = c.levelKg <= c.triggerKg
  const pending = c.status === 'pending'

  return (
    <>
      <div className="mt-6 grid gap-6 md:grid-cols-[1fr_360px]">
        <Card className="p-6 sm:p-8">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-2 text-sm">
            <span><b>{c.label || `${kg(c.cylinderKg)} cylinder`}</b> <span className="text-muted">· {c.business.name}, {c.outlet.name}</span></span>
            <span className="flex gap-3 text-xs">
              <button onClick={() => { const l = prompt('Name this cylinder (e.g. Home, Shop)', c.label ?? ''); if (l != null) rename.mutate(l.trim()) }} className="text-muted hover:text-ink">Rename</button>
              <button onClick={() => confirm(pending ? 'Withdraw this request?' : `Remove this cylinder from your account? ${c.business.name} keeps its records.`) && remove.mutate()} className="text-muted hover:text-danger">{pending ? 'Withdraw' : 'Remove'}</button>
            </span>
          </div>
          {pending ? (
            <div className="rounded-xl bg-page p-6 text-sm">
              <p className="font-semibold">Waiting for {c.business.name} to approve</p>
              <p className="mt-1 text-ink-2">{c.outlet.name} will confirm your {kg(c.cylinderKg)} cylinder, usually when you next visit. We'll email you once it's active, and your gas level appears here after they fit a meter.</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-6 sm:flex-row">
              <Ring value={c.levelKg} max={c.cylinderKg} size={210} color={ringColor(c)}>
                <div>
                  <p className="text-xs font-semibold text-[#637381]">Gas left</p>
                  <p className="num text-3xl leading-[48px] font-bold">{c.levelKg.toFixed(3)}kg</p>
                  <p className="text-xs text-muted">of {kg(c.cylinderKg)} · {c.cylinderKg ? Math.floor((c.levelKg / c.cylinderKg) * 1000) / 10 : 0}%</p>
                </div>
              </Ring>
              <div className="space-y-3 text-sm">
                <p className={cx('inline-block rounded-full px-3 py-1 text-xs font-bold text-white', low ? 'bg-danger' : c.level === 'full' ? 'bg-good-2' : 'bg-[#e8a33d]')}>{low ? 'Running low' : levelWord(c.level)}</p>
                {c.device ? (
                  <>
                    <p className="text-ink-2">We'll raise a refill automatically when you drop to <b className="text-ink">{c.triggerKg}kg</b>.</p>
                    <p className="text-xs text-muted">
                      {c.liveFlow != null && c.liveFlow > 0 && <span className="font-semibold text-good">● Gas in use now · </span>}
                      Meter last reported {c.device.lastSeenAt ? ago(c.device.lastSeenAt) : 'not yet'}
                    </p>
                  </>
                ) : <p className="text-ink-2">No meter fitted yet — this level is from your last refill. Ask {c.outlet.name} about a GasRail meter.</p>}
                {c.lastOrderAt && <p className="text-xs text-muted">Last refill {orderDate(c.lastOrderAt)}</p>}
              </div>
            </div>
          )}
        </Card>

        <Card className="bg-[linear-gradient(136deg,#f86822_0%,#dc5210_98%)] p-6">
          <h2 className="text-lg font-bold">Request a refill</h2>
          {pending ? <p className="mt-4 text-sm text-black/80">Available once {c.business.name} approves this cylinder.</p> : open ? (
            <div className="mt-4 space-y-3 text-sm">
              <p className="font-semibold">Refill of {kg(open.kg)} is {open.status.replace('_', ' ')}.</p>
              <p className="text-black/70">{nairaFull(open.amountKobo)} · requested {orderDate(open.createdAt)}</p>
              {open.status === 'pending' && <Button variant="dark" className="w-full" disabled={cancel.isPending} onClick={() => cancel.mutate(open.id)}>Cancel request</Button>}
            </div>
          ) : (
            <form className="mt-4 space-y-4" onSubmit={(e) => (e.preventDefault(), refill.mutate())}>
              <label className="block">
                <span className="mb-2 block text-xs text-black/85">How much gas (kg)</span>
                <input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} inputMode="decimal" className="num h-12 w-full border border-black/20 bg-transparent px-2 font-bold focus:border-black/60 focus:outline-none" />
              </label>
              <p className="text-sm">Estimated cost <b className="num">{naira((Number(amount) || 0) * c.pricePerKg)}</b> <span className="text-xs text-black/70">at {naira(c.pricePerKg)}/kg</span></p>
              <Button variant="dark" className="h-11 w-full" disabled={refill.isPending || !(Number(amount) > 0)}>{refill.isPending ? 'Sending…' : 'Request refill'}</Button>
            </form>
          )}
        </Card>
      </div>

      <div className="mt-8 grid gap-6 md:grid-cols-2">
        <Card className="p-6">
          <h2 className="mb-4 text-sm font-semibold">Orders</h2>
          <ul className="divide-y divide-black/5">
            {c.orders.map((o) => (
              <li key={o.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                <span><span className="num font-medium">{kg(o.kg)}</span> <span className="text-xs text-muted">· {orderDate(o.createdAt)}</span></span>
                <span className="flex items-center gap-3"><span className="num text-xs">{nairaFull(o.amountKobo)}</span><StatusPill status={o.status as Status} /></span>
              </li>
            ))}
            {!c.orders.length && <li className="py-4 text-sm text-muted">No orders yet.</li>}
          </ul>
        </Card>
        <Card className="p-6">
          <h2 className="mb-4 text-sm font-semibold">Activity</h2>
          <ul className="space-y-3">
            {c.activities.map((a) => <li key={a.id} className="text-sm">{a.text}<span className="block text-xs text-muted">{ago(a.at)}</span></li>)}
          </ul>
          <p className="mt-6 border-t border-black/5 pt-4 text-xs text-muted">Supplied by <b className="text-ink">{c.business.name}</b> · {c.outlet.name}{c.outlet.address ? `, ${c.outlet.address}` : ''}</p>
        </Card>
      </div>
    </>
  )
}

function AddCylinder({ onDone }: { onDone: (id: string) => void }) {
  const qc = useQueryClient()
  const { data } = useQuery(directoryQuery)
  const [bizId, setBizId] = useState('')
  const biz = data?.businesses.find((b) => b.id === bizId)
  const add = useMutation({
    mutationFn: (f: FormData) => call(api.portal.cylinders.$post({ json: {
      outletId: String(f.get('outletId')), cylinderKg: Number(f.get('cylinderKg')), label: String(f.get('label') || '') || undefined, address: String(f.get('address')),
    } })),
    onSuccess: (r) => (qc.invalidateQueries({ queryKey: ['portal'] }), toast('Sent to the business for approval'), onDone(r.id)),
  })
  return (
    <form onSubmit={(e) => (e.preventDefault(), add.mutate(new FormData(e.currentTarget)))} className="space-y-4">
      <FormError error={add.error?.message} />
      <Field label="Gas business">
        <Select value={bizId} onChange={(e) => setBizId(e.target.value)} required>
          <option value="" disabled>Choose your supplier</option>
          {data?.businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </Select>
      </Field>
      <Field label="Outlet you buy from">
        <Select name="outletId" key={bizId} defaultValue="" required disabled={!biz}>
          <option value="" disabled>{biz ? 'Choose outlet' : 'Choose a business first'}</option>
          {biz?.outlets.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.region}, {o.state}</option>)}
        </Select>
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Cylinder size">
          <Select name="cylinderKg" defaultValue="12.5">
            {CYLINDER_SIZES.filter((k) => k <= 100).map((k) => <option key={k} value={k}>{k}kg</option>)}
          </Select>
        </Field>
        <Field label="Name (optional)"><Input name="label" placeholder="Home, Shop…" maxLength={40} /></Field>
      </div>
      <Field label="Where the cylinder is" hint="So the outlet knows where to deliver"><Input name="address" required maxLength={240} placeholder="House number, street, area" /></Field>
      <div className="flex justify-end"><Button disabled={add.isPending}>{add.isPending ? 'Sending…' : 'Send for approval'}</Button></div>
    </form>
  )
}
